<template>
  <SoPageWithRightSidePanel v-model="current.isSidePanelCollapsed">
    <SoAlert
      v-if="pendingAgreements.length > 0"
      type="warning"
      title="Agreements Not Yet Started"
      class="ma-4"
    >
      <ul>
        <li v-for="agreement in pendingAgreements" :key="agreement.id">
          {{ agreement.productName }}, starts in
          {{ Math.abs(daysFromToday(agreement.startDate)) }} days (
          {{ formatDate(agreement.startDate) }}
          -
          {{
            agreement.endDate === null
              ? "onwards"
              : formatDate(agreement.endDate)
          }})
        </li>
      </ul>
    </SoAlert>
    <div v-if="homeCoverPromoEnabled && accountIsActive" class="pr-4 pl-4">
      <SoBanner data-testid="home-cover-promo-banner" rounded>
        <p>
          Check if the customer is interested in our
          <a target="_blank" :href="homeCoverInfoPage">HomeCover</a>
          deal. They will get £50 credit if they renew to fixed green tariff
          <b class="font-weight-medium">and</b>
          sign up to Home Cover with HomeTree. Remember to fill out this
          <a target="_blank" :href="homeCoverApplicationLink">form</a> if the
          customer is interested.
        </p>
      </SoBanner>
    </div>
    <SoContainer fluid>
      <SoAlert
        v-if="showElecVatRateChangeInfo"
        type="info"
        title="From 1 October, electricity VAT temporarily drops from 5% to 0%."
        data-testid="electricity-vat-change-banner"
        class="mb-4"
      >
        Prices shown reflect the lower rate, but until 1 October you'll still
        pay 5% on electricity.
      </SoAlert>
      <SoRow v-if="accountIsActive">
        <SoCol :default-width="6">
          <SoWidget :loading="isLoading" title="Meterpoints">
            <MeterpointsWidget v-if="!isLoading" />
          </SoWidget>
        </SoCol>
        <SoCol :default-width="6">
          <SoWidget :loading="isLoading" title="Quote">
            <QuoteWidget v-if="renewals.isLoaded" />
          </SoWidget>
        </SoCol>
      </SoRow>
      <SoAlert
        v-else
        type="warning"
        :title="`Account is ${accountStatus?.toLowerCase()}`"
      >
        This account is {{ accountStatus.toLowerCase() }} and therefore we can’t
        renew the customer to a new tariff.
      </SoAlert>
    </SoContainer>
    <template #sidePanel>
      <CustomerAccountInfoPanel></CustomerAccountInfoPanel>
    </template>
    <template #sidePanelRail>
      <CustomerAccountInfoRail></CustomerAccountInfoRail>
    </template>
  </SoPageWithRightSidePanel>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { AccountStatuses } from "@/models/account/account.types"
  import { IAgreement } from "@/models/agreement/agreement.types"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { useRenewalsStore } from "@/stores/renewals"
  import { formatDate, daysFromToday } from "@/helpers/dateHelper"
  import { FeaturesService } from "@/services/admin/FeaturesService"
  import { ConfigurationService } from "@/services/ConfigurationService"
  import {
    ElectricityVatChange,
    NO_ELECTRICITY_VAT_CHANGE,
    isElectricityVatChangeActive,
  } from "@/helpers/vatHelper"
  import { externalRoutes } from "@/constants/routes/externalRoutes"
  import CustomerAccountInfoPanel from "../overview/CustomerAccountInfoPanel.vue"
  import CustomerAccountInfoRail from "../overview/CustomerAccountInfoRail.vue"
  import MeterpointsWidget from "./widgets/MeterpointsWidget.vue"
  import QuoteWidget from "./widgets/QuoteWidget.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoBanner from "@/components/SoBanner.vue"
  import SoCol from "@/components/SoCol.vue"
  import SoContainer from "@/components/SoContainer.vue"
  import SoPageWithRightSidePanel from "@/components/templates/SoPageWithRightSidePanel.vue"
  import SoRow from "@/components/SoRow.vue"
  import SoWidget from "@/components/SoWidget.vue"

  export default defineComponent({
    components: {
      SoContainer,
      SoPageWithRightSidePanel,
      CustomerAccountInfoPanel,
      CustomerAccountInfoRail,
      SoRow,
      SoCol,
      MeterpointsWidget,
      QuoteWidget,
      SoBanner,
      SoWidget,
      SoAlert,
    },
    data() {
      return {
        renewals: useRenewalsStore(),
        current: useCurrentAccountStore(),
        homeCoverApplicationLink: externalRoutes.HomeCoverApplication.path,
        homeCoverInfoPage: externalRoutes.HomeCoverInfoPage.path,
        AccountStatuses,
        formatDate,
        daysFromToday,
      }
    },
    computed: {
      isLoading() {
        return (
          this.current.meterpointTariffs.isFetching ||
          this.current.renewalPeriod.isFetching ||
          ConfigurationService.getElectricityVatChangeFrom().isFetching ||
          ConfigurationService.getElectricityVatChangeNewRate().isFetching
        )
      },
      electricityVatChange(): ElectricityVatChange {
        const from = ConfigurationService.getElectricityVatChangeFrom().data
        const newRate =
          ConfigurationService.getElectricityVatChangeNewRate().data
        return newRate != null && isElectricityVatChangeActive(from)
          ? { active: true, newRate }
          : NO_ELECTRICITY_VAT_CHANGE
      },
      showElecVatRateChangeInfo(): boolean {
        return (
          FeaturesService.isFeatureActive("SHOW_ELEC_VAT_RATE_CHANGE_INFO") ??
          false
        )
      },
      homeCoverPromoEnabled() {
        return FeaturesService.getFeaturesAdminList().data.find(
          (feature) => feature.name === "HOME_COVER_PROMO",
        )?.enabled
      },
      accountStatus() {
        return this.current.account.data?.status.text
      },
      accountIsActive() {
        return this.accountStatus === AccountStatuses.ACTIVE
      },
      pendingAgreements() {
        return Object.values(this.current.meterpointAgreements.data)
          .flat()
          .filter((item: IAgreement) => item.isPending)
      },
    },
    watch: {
      isLoading() {
        this.initialiseRenewals()
      },
    },
    mounted() {
      this.initialiseRenewals()
    },
    methods: {
      initialiseRenewals() {
        if (!this.isLoading && this.accountIsActive) {
          this.renewals.initialiseMeterpoints(
            this.current.meterpointTariffs.data,
            this.electricityVatChange,
          )
        }
      },
    },
  })
</script>
